import { useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Download,
  Search,
  ShieldAlert,
  ShieldCheck,
  Table2,
} from "lucide-react";
import { downloadCsv, toCsv } from "@/lib/csv";
import { type CellKind, detectColumnKind, formatCell } from "@/lib/format";

type Props = {
  columns: string[];
  rows: unknown[][];
  question: string;
  validated?: boolean;           // narrative numbers cross-checked against rows
  validationNote?: string;       // shown when validated === false
  /**
   * Optional human-readable title shown after the "Table" label —
   * "Table · Search results", "Table · Folder contents", etc.
   * Without it the header just reads "TABLE" which is uninformative
   * when an answer renders multiple tables stacked.
   */
  title?: string;
  /**
   * The MCP tool that produced this table (e.g. `prod:searchContent`).
   * Rendered as a small monospace pill next to the row count so a power
   * user can tell which tool the rows came from at a glance — useful
   * when debugging why the agent picked one tool over another.
   */
  tool?: string;
};

export function ResultsTable({ columns, rows, question, validated, validationNote, title, tool }: Props) {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);

  const kinds: CellKind[] = useMemo(
    () => columns.map((_, i) => detectColumnKind(rows.map((r) => r[i]))),
    [columns, rows],
  );

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.some((v) => String(v ?? "").toLowerCase().includes(needle)));
  }, [rows, filter]);

  const sorted = useMemo(() => {
    if (sortCol == null) return filtered;
    const kind = kinds[sortCol];
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      let cmp: number;
      if (kind === "number") {
        cmp = Number(av) - Number(bv);
      } else if (kind === "date") {
        cmp = new Date(String(av)).getTime() - new Date(String(bv)).getTime();
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortCol, sortDir, kinds]);

  function toggleSort(i: number) {
    if (sortCol === i) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(i);
      setSortDir("asc");
    }
  }

  function exportCsv() {
    const csv = toCsv(columns, sorted);
    const name = `cloudcraftiq-operate-${question.slice(0, 24).replace(/\W+/g, "-")}.csv`;
    downloadCsv(name, csv);
  }

  function copyTsv() {
    const tsv = [
      columns.map(humanize).join("\t"),
      ...sorted.map((r) => r.map((v, c) => stringifyCell(v, kinds[c])).join("\t")),
    ].join("\n");
    navigator.clipboard.writeText(tsv).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      },
      () => {
        /* clipboard refused — best-effort, nothing to surface */
      },
    );
  }

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-datumcraft-slate/25 bg-white shadow-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-datumcraft-slate/15 bg-datumcraft-fog/40 px-3 py-1.5 text-sm">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-datumcraft-slate">
          <Table2 size={12} aria-hidden /> Table
        </div>
        {title && (
          <span
            className="truncate text-[12px] font-semibold normal-case text-datumcraft-navy"
            title={title}
          >
            · {title}
          </span>
        )}

        <div className="relative">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-datumcraft-slate"
            aria-hidden
          />
          <input
            aria-label="filter rows"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-44 rounded-md border border-datumcraft-slate/30 bg-white py-0.5 pl-6 pr-2 text-xs"
          />
        </div>

        <span className="text-2xs text-datumcraft-slate">
          <span className="font-semibold text-datumcraft-ink">{sorted.length.toLocaleString()}</span>
          {" "}of {rows.length.toLocaleString()} rows
        </span>

        {tool && (
          <span
            title={`Source tool: ${tool}`}
            className="rounded-full border border-datumcraft-slate/25 bg-white px-2 py-0.5 font-mono text-[10px] font-medium text-datumcraft-steel"
          >
            {tool}
          </span>
        )}

        {validated === true && (
          <span
            title="Numbers in the narrative were cross-checked against the returned rows."
            className="inline-flex items-center gap-1 rounded-full bg-datumcraft-success/10 px-2 py-0.5 text-2xs font-medium text-datumcraft-success"
          >
            <ShieldCheck size={11} /> Validated
          </span>
        )}
        {validated === false && (
          <span
            title={validationNote ?? "Some numbers in the narrative weren't found in the returned rows."}
            className="inline-flex items-center gap-1 rounded-full bg-datumcraft-warn/15 px-2 py-0.5 text-2xs font-medium text-datumcraft-warn"
          >
            <ShieldAlert size={11} /> Review numbers
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={copyTsv}
            title="Copy as TSV (paste into Excel, Sheets, …)"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-datumcraft-slate hover:bg-white hover:text-datumcraft-navy"
          >
            <Copy size={11} aria-hidden /> {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            title="Download as CSV"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-datumcraft-slate hover:bg-white hover:text-datumcraft-navy"
          >
            <Download size={11} aria-hidden /> CSV
          </button>
        </div>
      </div>

      <div className="max-h-[420px] overflow-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  scope="col"
                  className={`sticky top-0 border-b border-datumcraft-slate/30 bg-datumcraft-fog/95 px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-wide text-datumcraft-navy backdrop-blur ${
                    kinds[i] === "number" ? "text-right" : ""
                  }`}
                >
                  <button
                    onClick={() => toggleSort(i)}
                    className={`inline-flex items-center gap-1 hover:text-datumcraft-accentDark ${
                      kinds[i] === "number" ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span>{humanize(c)}</span>
                    {sortCol === i ? (
                      sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
                    ) : (
                      <ArrowUpDown size={11} className="opacity-40" />
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, r) => (
              <tr
                key={r}
                className="odd:bg-white even:bg-datumcraft-fog/40 hover:bg-datumcraft-mist/60"
              >
                {row.map((v, c) => (
                  <td
                    key={c}
                    className={`border-b border-datumcraft-slate/15 px-3 py-1.5 align-top text-[13px] text-datumcraft-ink ${
                      kinds[c] === "number" ? "text-right tabular-nums font-medium" : ""
                    } ${kinds[c] === "text" ? "max-w-[360px] truncate" : ""}`}
                    title={kinds[c] === "text" ? String(v ?? "") : undefined}
                  >
                    {kinds[c] === "boolean" ? (
                      <BoolPill on={v === true || v === "true" || v === 1} />
                    ) : (
                      formatCell(v, kinds[c])
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-sm text-datumcraft-slate">
                  No rows match “{filter}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BoolPill({ on }: { on: boolean }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-2xs font-medium ${
        on ? "bg-datumcraft-success/15 text-datumcraft-success" : "bg-datumcraft-slate/15 text-datumcraft-slate"
      }`}
    >
      {on ? "Yes" : "No"}
    </span>
  );
}

function humanize(col: string): string {
  return col.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Stringify a single cell for clipboard copy. Reuses formatCell so the
 * pasted text matches the rendered text — including the "—" em-dash for
 * empty cells and Yes/No for booleans (pills don't survive a TSV
 * roundtrip into Excel).
 */
function stringifyCell(v: unknown, kind: CellKind): string {
  return formatCell(v, kind);
}
